import React, { useState, useEffect } from 'react'

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import {
  getDevToolIcon,
  getInstanceUrlPlaceholder,
  getInstanceUrlLabel,
  getInstanceUrlHelpText,
  createProductBoardFeature,
  getStoredInstanceUrl,
  saveInstanceUrl,
  getTaskCreationUrl,
} from '@/components/utils/taskCreationUtils'
import type { DevTool } from '@/components/dashboard/roadmap/DevToolSelect'

interface InstanceConfigDialogProps {
  isOpen: boolean
  onClose: () => void
  devTool: DevTool
  title: string
  description?: string
  dataPoints?: number
}

const InstanceConfigDialog = ({
  isOpen,
  onClose,
  devTool,
  title,
  description,
  dataPoints = 0,
}: InstanceConfigDialogProps) => {
  const [instanceUrl, setInstanceUrl] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (isOpen) {
      const storedUrl = getStoredInstanceUrl(devTool)
      setInstanceUrl(storedUrl || '')
    }
  }, [isOpen, devTool])

  const handleSaveInstanceConfig = () => {
    if (instanceUrl.trim()) {
      saveInstanceUrl(devTool, instanceUrl)

      if (devTool === 'productboard') {
        handleCreateProductBoardFeature(instanceUrl)
      } else {
        openTaskCreationUrl()
      }
    } else {
      toast({
        title: 'Error',
        description: 'Please enter a valid instance URL',
        variant: 'destructive',
      })
    }
  }

  const openTaskCreationUrl = () => {
    const url = getTaskCreationUrl(devTool, instanceUrl, title, description, dataPoints)

    if (url) {
      window.open(url, '_blank')

      toast({
        title: 'Link opened',
        description: `Creating a new task in ${devTool.charAt(0).toUpperCase() + devTool.slice(1)}`,
      })

      onClose()
    }
  }

  const handleCreateProductBoardFeature = async (apiKey: string) => {
    if (!apiKey.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a valid ProductBoard API key',
        variant: 'destructive',
      })
      return
    }

    setIsCreating(true)

    try {
      const additionalContent = `Our comprehensive analysis has revealed several key insights that make this opportunity particularly compelling:

First, we've identified a clear pattern in user behavior that indicates a strong demand for this functionality. Users consistently attempt to accomplish these tasks through workarounds, suggesting a natural product evolution.

Second, our market research shows that implementing this feature would not only address current user needs but also position us advantageously against competitors. Similar implementations in other platforms have shown significant improvements in user engagement and satisfaction metrics.

Furthermore, the technical foundation required for this implementation aligns well with our current architecture, making this an opportune time to proceed with development. The proposed solution is designed to be scalable and maintainable, with clear upgrade paths for future enhancements.`

      const keyElements = [
        'Implement AI-powered recommendation engine',
        'Develop user behavior tracking system',
        'Create personalized content delivery system',
        'Build A/B testing framework',
        'Design analytics dashboard',
      ]
        .map((element) => `- ${element}`)
        .join('\n')

      const formattedDescription = `${description || ''}\n\n${additionalContent}\n\nKey Elements:\n${keyElements}`

      await createProductBoardFeature(apiKey, title, formattedDescription)

      toast({
        title: 'Success',
        description: `Feature created in ProductBoard successfully!`,
      })

      onClose()
    } catch (error) {
      console.error('Error creating ProductBoard feature:', error)
      toast({
        title: 'Error',
        description: `Failed to create feature in ProductBoard. Please check your API key.`,
        variant: 'destructive',
      })
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center space-x-2">
            <img src={getDevToolIcon(devTool)} alt={devTool} className="h-6 w-auto" />
            <DialogTitle>Configure {devTool.charAt(0).toUpperCase() + devTool.slice(1)} Instance</DialogTitle>
          </div>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="instanceUrl" className="text-right">
              {getInstanceUrlLabel(devTool)}
            </Label>
            <Input
              id="instanceUrl"
              placeholder={getInstanceUrlPlaceholder(devTool)}
              className="col-span-3"
              value={instanceUrl}
              onChange={(e) => setInstanceUrl(e.target.value)}
            />
          </div>
          <div className="text-sm text-gray-500 mt-2">{getInstanceUrlHelpText(devTool)}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSaveInstanceConfig} disabled={isCreating}>
            {isCreating ? 'Processing...' : 'Save & Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default InstanceConfigDialog
